// src/components/forms/TaskMarksForm.jsx (Updated)
import React, { useState, useEffect } from 'react';
import { Upload, Download, PlusCircle, X, Calculator, UserCheck, Check, AlertCircle } from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { StatisticsCalculator } from '../../utils/statistics';

const TaskMarksForm = ({ onSubmit, selectedSubmission = null }) => {
  const [submissions, setSubmissions] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [existingLearners, setExistingLearners] = useState([]);
  const [allSchoolLearners, setAllSchoolLearners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    submissionId: selectedSubmission?.id || '',
    taskId: selectedSubmission?.taskId || '',
    learners: []
  });

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    if (selectedSubmission) {
      loadTaskForSubmission(selectedSubmission.taskId);
      loadExistingLearners(selectedSubmission.grade, selectedSubmission.subject);
    }
  }, [selectedSubmission]);

  const loadSubmissions = async () => {
    try {
      const submissionList = await AssessmentService.getTaskSubmissions();
      setSubmissions(submissionList);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const loadTaskForSubmission = async (taskId) => {
    try {
      const tasks = await AssessmentService.getAssessmentTasks();
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTask(task);
      }
    } catch (error) {
      console.error('Error loading task:', error);
    }
  };

  const loadExistingLearners = async (grade, subject) => {
    try {
      setLoading(true);
      
      // Load learners who already have marks for this task
      const existingMarks = await AssessmentService.getLearnerMarks(selectedSubmission?.taskId, selectedSubmission?.id);
      const existingLearnersWithMarks = existingMarks.map(mark => ({
        id: mark.id,
        learnerSurname: mark.learnerSurname,
        learnerName: mark.learnerName,
        school: mark.school || '',
        totalMarks: mark.totalMarks,
        learnerMark: mark.learnerMark,
        percentageMark: mark.percentageMark,
        performanceLevel: mark.performanceLevel || StatisticsCalculator.calculatePerformanceLevel(mark.percentageMark),
        markQuartile: mark.markQuartile,
        hasSubmitted: mark.hasSubmitted !== false, // Default to true for existing marks
        isExistingLearner: true,
        learnerId: mark.learnerId,
        existingMarkId: mark.id
      }));

      // Load all school learners for this grade and subject
      const allLearners = await AssessmentService.getLearners(grade, subject);
      setAllSchoolLearners(allLearners);
      
      if (existingLearnersWithMarks.length > 0) {
        // Use existing learners with marks
        setFormData(prev => ({
          ...prev,
          learners: existingLearnersWithMarks
        }));
        setExistingLearners(existingLearnersWithMarks);
      } else {
        // No existing marks, populate with all school learners
        const schoolLearners = allLearners
          .sort((a, b) => a.learnerSurname.localeCompare(b.learnerSurname))
          .map(learner => ({
            learnerSurname: learner.learnerSurname,
            learnerName: learner.learnerName,
            school: learner.school || '',
            totalMarks: selectedTask?.totalMarks || 0,
            learnerMark: 0,
            percentageMark: 0,
            performanceLevel: 'L1',
            markQuartile: 'Q1',
            hasSubmitted: true, // Default to submitted
            isExistingLearner: true,
            learnerId: learner.id
          }));
        
        setFormData(prev => ({
          ...prev,
          learners: schoolLearners
        }));
        setExistingLearners([]);
      }
    } catch (error) {
      console.error('Error loading learners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmissionChange = async (submissionId) => {
    const submission = submissions.find(s => s.id === submissionId);
    if (submission) {
      setFormData(prev => ({
        ...prev,
        submissionId: submission.id,
        taskId: submission.taskId,
        learners: []
      }));
      await loadTaskForSubmission(submission.taskId);
      await loadExistingLearners(submission.grade, submission.subject);
    }
  };

  const addLearner = () => {
    const newLearner = {
      learnerSurname: '',
      learnerName: '',
      school: '',
      totalMarks: selectedTask?.totalMarks || 0,
      learnerMark: 0,
      percentageMark: 0,
      performanceLevel: 'L1',
      markQuartile: 'Q1',
      hasSubmitted: true,
      isExistingLearner: false,
      learnerId: null
    };
    
    setFormData(prev => ({
      ...prev,
      learners: [...prev.learners, newLearner]
    }));
  };

  const removeLearner = (index) => {
    setFormData(prev => ({
      ...prev,
      learners: prev.learners.filter((_, i) => i !== index)
    }));
  };

  const updateLearner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      learners: prev.learners.map((learner, i) => {
        if (i === index) {
          const updated = { ...learner, [field]: value };
          
          // Auto-calculate percentage and level when learner mark changes
          if (field === 'learnerMark') {
            const mark = parseInt(value) || 0;
            updated.percentageMark = updated.totalMarks > 0 ? (mark / updated.totalMarks) * 100 : 0;
            updated.performanceLevel = StatisticsCalculator.calculatePerformanceLevel(updated.percentageMark);
            updated.markQuartile = StatisticsCalculator.calculateMarkQuartile(updated.percentageMark);
          }
          
          // If hasSubmitted is false, set mark to 0
          if (field === 'hasSubmitted' && !value) {
            updated.learnerMark = 0;
            updated.percentageMark = 0;
            updated.performanceLevel = 'L1';
            updated.markQuartile = 'Q1';
          }
          
          return updated;
        }
        return learner;
      })
    }));
  };

  const selectExistingLearner = (learnerIndex, learnerId) => {
    const existingLearner = allSchoolLearners.find(l => l.id === learnerId);
    if (existingLearner) {
      setFormData(prev => ({
        ...prev,
        learners: prev.learners.map((learner, i) => 
          i === learnerIndex 
            ? {
                ...learner,
                learnerSurname: existingLearner.learnerSurname,
                learnerName: existingLearner.learnerName,
                school: existingLearner.school || '',
                isExistingLearner: true,
                learnerId: existingLearner.id
              }
            : learner
        )
      }));
    }
  };

  const calculateSubmittedLearners = () => {
    return formData.learners.filter(l => l.hasSubmitted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const submission = submissions.find(s => s.id === formData.submissionId);
      
      // Create/update learner marks with learner records
      for (const learner of formData.learners) {
        const learnerData = {
          learnerSurname: learner.learnerSurname,
          learnerName: learner.learnerName,
          school: learner.school || '', // You might want to get this from user profile
          grade: submission.grade,
          subject: submission.subject
        };

        const markData = {
          taskId: formData.taskId,
          submissionId: formData.submissionId,
          learnerSurname: learner.learnerSurname,
          learnerName: learner.learnerName,
          school: learner.school || '',
          totalMarks: learner.totalMarks,
          learnerMark: learner.learnerMark,
          percentageMark: learner.percentageMark,
          performanceLevel: learner.performanceLevel,
          markQuartile: learner.markQuartile,
          hasSubmitted: learner.hasSubmitted,
          hasItemisedMarks: false
        };

        if (learner.existingMarkId) {
          // Update existing mark
          await AssessmentService.updateLearnerMark(learner.existingMarkId, markData);
        } else if (learner.isExistingLearner && learner.learnerId) {
          // Use existing learner
          markData.learnerId = learner.learnerId;
          await AssessmentService.createLearnerMark(markData);
        } else {
          // Create new learner record and mark
          await AssessmentService.createLearnerMarkWithLearner(markData, learnerData);
        }
      }

      onSubmit(formData.learners);
    } catch (error) {
      console.error('Error saving learner marks:', error);
    } finally {
      setLoading(false);
    }
  };

  const submittedLearners = calculateSubmittedLearners();
  const submissionRate = formData.learners.length > 0 ? (submittedLearners.length / formData.learners.length) * 100 : 0;

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        <Calculator className="w-6 h-6" />
        Record Learner Marks
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Submission Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Task Submission *
            </label>
            <select
              value={formData.submissionId}
              onChange={(e) => handleSubmissionChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loading}
            >
              <option value="">Select submission...</option>
              {submissions.map(submission => (
                <option key={submission.id} value={submission.id}>
                  {submission.grade} {submission.subject} - {submission.term} ({new Date(submission.date).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end gap-2">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-blue-700">
                <div><strong>Submitted:</strong> {submittedLearners.length} / {formData.learners.length}</div>
                <div><strong>Rate:</strong> {submissionRate.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Task Information Display */}
        {selectedTask && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">Task: {selectedTask.name}</h3>
            <p className="text-sm text-gray-600">
              Total Marks: {selectedTask.totalMarks} | Questions: {selectedTask.questions.length}
            </p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading learners...</span>
          </div>
        )}

        {/* Learners Table */}
        {!loading && formData.learners.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Submitted</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Existing Learner</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Surname</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Name(s)</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">School</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Total Marks</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Learner Mark</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">%</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Level</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Quartile</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {formData.learners.map((learner, learnerIndex) => (
                  <tr key={learnerIndex} className={`hover:bg-gray-50 ${!learner.hasSubmitted ? 'bg-red-50' : ''}`}>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={learner.hasSubmitted}
                        onChange={(e) => updateLearner(learnerIndex, 'hasSubmitted', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2">
                      <select
                        value={learner.learnerId || ''}
                        onChange={(e) => e.target.value ? selectExistingLearner(learnerIndex, e.target.value) : null}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                        disabled={learner.existingMarkId}
                      >
                        <option value="">New learner</option>
                        {allSchoolLearners.map(existingLearner => (
                          <option key={existingLearner.id} value={existingLearner.id}>
                            {existingLearner.learnerSurname}, {existingLearner.learnerName}
                          </option>
                        ))}
                      </select>
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="text"
                        value={learner.learnerSurname}
                        onChange={(e) => updateLearner(learnerIndex, 'learnerSurname', e.target.value)}
                        className={`w-full px-2 py-1 border border-gray-200 rounded text-sm ${
                          learner.isExistingLearner ? 'bg-gray-100' : ''
                        }`}
                        placeholder="Surname"
                        readOnly={learner.isExistingLearner}
                      />
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="text"
                        value={learner.learnerName}
                        onChange={(e) => updateLearner(learnerIndex, 'learnerName', e.target.value)}
                        className={`w-full px-2 py-1 border border-gray-200 rounded text-sm ${
                          learner.isExistingLearner ? 'bg-gray-100' : ''
                        }`}
                        placeholder="First name(s)"
                        readOnly={learner.isExistingLearner}
                      />
                    </td>

                    <td className="border border-gray-300 px-3 py-2">
                        <input
                          type="text"
                          value={learner.school || ''}
                          onChange={(e) => updateLearner(learnerIndex, 'school', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="School name"
                        />
                      </td>
                    
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <span className="text-sm font-medium">{learner.totalMarks}</span>
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2">
                      <input
                        type="number"
                        value={learner.learnerMark}
                        onChange={(e) => updateLearner(learnerIndex, 'learnerMark', parseInt(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-center"
                        min="0"
                        max={learner.totalMarks}
                        disabled={!learner.hasSubmitted}
                      />
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <span className="text-sm font-medium">{learner.percentageMark.toFixed(1)}%</span>
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-1 rounded ${StatisticsCalculator.getPerformanceLevelColor(learner.performanceLevel)}`}>
                        {learner.performanceLevel}
                      </span>
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-1 rounded ${
                        learner.markQuartile === 'Q4' ? 'bg-green-100 text-green-800' :
                        learner.markQuartile === 'Q3' ? 'bg-blue-100 text-blue-800' :
                        learner.markQuartile === 'Q2' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {learner.markQuartile}
                      </span>
                    </td>
                    
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {formData.learners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLearner(learnerIndex)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Learner Button */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={addLearner}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusCircle className="w-4 h-4" />
            Add Learner
          </button>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading || formData.learners.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                'Save All Marks'
              )}
            </button>
          </div>
        </div>

        {/* Performance Level Legend */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-3">Performance Levels</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            {['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7'].map(level => (
              <div key={level} className={`px-2 py-1 rounded text-center ${StatisticsCalculator.getPerformanceLevelColor(level)}`}>
                <div className="font-medium">{level}</div>
                <div>{StatisticsCalculator.getPerformanceLevelDescription(level).split(' (')[0]}</div>
              </div>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
};

export default TaskMarksForm;